/**
 * TreeMapContextAdapterOrganism
 *
 * Bridges context execution data to a hierarchical tree-map rendering.
 * Fetches a context view config and context execution results,
 * then maps them to a level-based hierarchical display.
 */

import React, { useMemo } from 'react';
import { useTranslation } from 'localization';
import { useContextData } from '../../../hooks/useContextData';
import { useContextView } from '../../../hooks/useContextView';
import type { ApiClients } from '../../../context/ApiClientsContext';

export interface TreeMapContextAdapterOrganismProps {
  /** API clients from context */
  apiClients: ApiClients | null;
  /** Context view system name to load */
  viewSystemName: string;
  /** Root sphere ID to execute context from */
  rootSphereId: string;
  /** Results per page per level */
  limit?: number;
  /** Max depth for repeat levels */
  depth?: number;
  /** Callback when a sphere node is selected */
  onNodeSelect?: (sphereId: string) => void;
  /** Callback when creating a new sphere */
  onNodeCreate?: (parentId: string, levelIndex: number) => void;
  /** Custom render for loading state */
  renderLoading?: () => React.ReactNode;
  /** Custom render for error state */
  renderError?: (error: string) => React.ReactNode;
  /** CSS class name */
  className?: string;
}

const TreeMapContextAdapterOrganism: React.FC<TreeMapContextAdapterOrganismProps> = ({
  apiClients,
  viewSystemName,
  rootSphereId,
  limit,
  depth,
  onNodeSelect,
  onNodeCreate,
  renderLoading,
  renderError,
  className,
}) => {
  const { t } = useTranslation();
  const { view, loading: viewLoading, error: viewError } = useContextView(apiClients, viewSystemName);

  const contextSystemName = view?.context_ref ?? '';

  const {
    data,
    loading: dataLoading,
    error: dataError,
    cacheMetadata,
    refresh,
    fetchMore,
    hasMore,
  } = useContextData(apiClients, {
    systemName: contextSystemName,
    rootSphereId,
    limit,
    depth,
  });

  const loading = viewLoading || dataLoading;
  const error = viewError || dataError;

  const treeMapConfig = useMemo(() => {
    if (!view) return null;
    const vc = view.view_config;
    return {
      defaultCardView: (vc.defaultCardView as string) ?? 'standard',
      allowCardViewToggle: vc.allowCardViewToggle as boolean | undefined,
      columnWidth: vc.columnWidth as number | undefined,
      columnGap: vc.columnGap as number | undefined,
      allowDragDrop: vc.allowDragDrop as boolean | undefined,
      showMinimap: vc.showMinimap as boolean | undefined,
    };
  }, [view]);

  const cardDensity = view?.base_config?.cardDensity ?? 'standard';

  if (loading && !data) {
    if (renderLoading) return <>{renderLoading()}</>;
    return <div className={className}>{t('context.common.loadingTreeMap')}</div>;
  }

  if (error && !data) {
    if (renderError) return <>{renderError(error)}</>;
    return <div className={className}>{t('context.common.error', { message: error })}</div>;
  }

  if (!data || !view) {
    return null;
  }

  const columnStyle: React.CSSProperties = {
    width: treeMapConfig?.columnWidth ? `${treeMapConfig.columnWidth}px` : undefined,
    gap: treeMapConfig?.columnGap ? `${treeMapConfig.columnGap}px` : undefined,
  };

  return (
    <div className={className} data-testid="treemap-context-adapter">
      <div className="treemap-context-adapter__header">
        <span className="treemap-context-adapter__title">
          {view.name?.default ?? view.system_name}
        </span>
        <button
          className="treemap-context-adapter__refresh"
          onClick={refresh}
          title={t('context.common.refreshData')}
          type="button"
        >
          ↻
        </button>
      </div>
      <div className="treemap-context-adapter__columns" style={columnStyle}>
        {Object.entries(data.spheresByColumn).map(([colIndex, spheres]) => {
          const level = data.context.levels[Number(colIndex)];
          return (
            <div key={colIndex} className="treemap-context-adapter__column">
              <div className="treemap-context-adapter__column-header">
                {level?.label ?? t('context.common.level', { index: colIndex })}
                <span className="treemap-context-adapter__column-count">
                  ({spheres.length})
                </span>
              </div>
              <div className="treemap-context-adapter__cards">
                {spheres.map((sphere) => (
                  <button
                    type="button"
                    key={sphere.id}
                    className={`treemap-context-adapter__card treemap-context-adapter__card--${cardDensity}`}
                    onClick={() => onNodeSelect?.(sphere.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') onNodeSelect?.(sphere.id);
                    }}
                    tabIndex={0}
                  >
                    <span className="treemap-context-adapter__card-label">
                      {String(sphere.label ?? sphere.id)}
                    </span>
                    {sphere.schemaType && (
                      <span className="treemap-context-adapter__card-type">
                        {sphere.schemaType}
                      </span>
                    )}
                  </button>
                ))}
                {onNodeCreate && (
                  <button
                    className="treemap-context-adapter__add"
                    onClick={() => onNodeCreate(rootSphereId, Number(colIndex))}
                    title={t('context.common.addSphere')}
                    type="button"
                  >
                    +
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {hasMore && (
        <button
          className="treemap-context-adapter__load-more"
          onClick={fetchMore}
          type="button"
        >
          {t('context.common.loadMore')}
        </button>
      )}
      {cacheMetadata && (
        <div className="treemap-context-adapter__cache-info" data-testid="cache-info">
          {cacheMetadata.structureHit ? t('context.common.cached') : t('context.common.fresh')} · {data.stats.totalLoaded} {t('context.common.spheres')}
        </div>
      )}
    </div>
  );
};

export default TreeMapContextAdapterOrganism;
